import { useState } from 'react';

import Modal from '../../components/modal/modal.component';
import WarningModal from '../../components/warning-modal/warning-modal.component';
import CustomInput from '../../components/custom-input/custom-input.component';
import { Vehicle } from '../../types/vehicle.type';
import { userSdk } from '../../services/user.service';
import { validateSchema } from '../../utils/validate-schema';
import {
    VehicleForm,
    vehicleFormSchema,
} from '../../validations/vehicle-form.schema';

import styles from './add-vehicle-modal.module.scss';

type Props = {
    vehicle: Vehicle;
    onDone: () => void;
};

const EditVehicleModal = ({ vehicle, onDone }: Props) => {
    const [form, setForm] = useState<VehicleForm>({
        brand: vehicle.brand,
        model: vehicle.model,
        color: vehicle.color,
        plate: vehicle.plate ?? '',
        manufactureYear:
            vehicle.manufactureYear != null
                ? String(vehicle.manufactureYear)
                : '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showWarning, setShowWarning] = useState(vehicle.insurance != null);
    const [showYearWarning, setShowYearWarning] = useState(false);
    const [yearChangeUnderstood, setYearChangeUnderstood] = useState(false);

    const updateField = (attr: keyof VehicleForm, value: string) => {
        setForm((prev) => ({ ...prev, [attr]: value }));
    };

    const onUpdate = async (skipYearWarning = false) => {
        if (loading) return;

        setError('');
        const errors = validateSchema(form, vehicleFormSchema);
        if (errors) return setError([...errors.values()].join('. '));

        const payload = vehicleFormSchema.cast(form);
        if (
            !skipYearWarning &&
            payload.manufactureYear !== vehicle.manufactureYear
        ) {
            setShowYearWarning(true);
            return;
        }

        setLoading(true);
        try {
            const response = await userSdk.updateVehicle(vehicle.id, payload);

            if ('error' in response) return setError(response.error);

            onDone();
        } finally {
            setLoading(false);
        }
    };

    if (showWarning) {
        return (
            <WarningModal
                title="Vehículo con seguro activo"
                body="Este vehículo tiene un seguro activo. ¿Deseas continuar con la edición?"
                note="Esta acción no puede ser revertida."
                onConfirm={() => setShowWarning(false)}
                onDiscard={onDone}
                confirmText="Continuar"
                discardText="Cancelar"
            />
        );
    }

    if (showYearWarning) {
        return (
            <WarningModal
                title="Cambio de año de fabricación"
                body="Cambiar el año de fabricación puede afectar la elegibilidad del servicio de grúa y la cobertura del seguro. ¿Deseas continuar?"
                checkbox={{
                    label: 'Entiendo que este cambio puede afectar el servicio de grúa y el seguro del vehículo.',
                    checked: yearChangeUnderstood,
                    onChange: setYearChangeUnderstood,
                }}
                onConfirm={() => {
                    setShowYearWarning(false);
                    void onUpdate(true);
                }}
                onDiscard={onDone}
                confirmText="Continuar"
                confirmDisabled={!yearChangeUnderstood}
                discardText="Cancelar"
            />
        );
    }

    return (
        <Modal onConfirm={onUpdate} onDiscard={onDone} confirmLoading={loading}>
            <div className={styles.modal}>
                <h4>Editar vehículo</h4>
                <div className={styles.row}>
                    <div className={styles.inputWrapper}>
                        <label>Marca</label>
                        <CustomInput
                            type={'text'}
                            value={form.brand}
                            onChange={(brand) => updateField('brand', brand)}
                        />
                    </div>
                    <div className={styles.inputWrapper}>
                        <label>Modelo</label>
                        <CustomInput
                            type={'text'}
                            value={form.model}
                            onChange={(model) => updateField('model', model)}
                        />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.inputWrapper}>
                        <label>Color</label>
                        <CustomInput
                            type={'text'}
                            value={form.color}
                            onChange={(color) => updateField('color', color)}
                        />
                    </div>
                    <div className={styles.inputWrapper}>
                        <label>Placa</label>
                        <CustomInput
                            type={'text'}
                            value={form.plate}
                            max={7}
                            onChange={(plate) => updateField('plate', plate)}
                        />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.inputWrapper}>
                        <label>Año de fabricación</label>
                        <CustomInput
                            type={'number'}
                            value={form.manufactureYear}
                            onChange={(year) =>
                                updateField('manufactureYear', year)
                            }
                        />
                    </div>
                </div>
                {error && <span className={styles.errorMessage}>{error}</span>}
            </div>
        </Modal>
    );
};

export default EditVehicleModal;
